import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import signatureSets from '../data/signatureSets';
import { formatPrice as formatPriceCents } from '../utils/money';
import { useCart } from '../contexts/CartContext';
import { imgSrc } from '../utils/img';
import Lightbox from '../components/Lightbox';
import Footer from '../components/Footer';
import '../pages/ProductDetail.css';
import useOnHandProducts from '../hooks/useOnHandProducts';

function parsePriceToCents(p) {
  if (typeof p === 'number') {
    return p > 1000 ? p : Math.round(p * 100);
  }
  if (typeof p === 'string') {
    const num = Number(p.replace(/[^0-9.]/g, ''));
    return Math.round(num * 100);
  }
  return null;
}

function badgeFromTags(tags = []) {
  const lower = tags.map(t => (t || '').toString().toLowerCase());
  if (lower.includes('holiday')) return 'Holiday';
  if (lower.includes('celeb-inspired') || lower.includes('celebrity')) return 'Celeb-Inspired';
  return null;
}

export default function Featured() {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '' });

  // ✅ use dynamic on-hand products instead of static data
  const onHandProducts = useOnHandProducts();

  // 1) Merge featured signature + on-hand items
  const featuredItems = useMemo(() => {
    const sig = signatureSets
      .filter(s => s.featured || s.tags?.some(t => /holiday|celeb/i.test(t)))
      .map(s => ({
        type: 'signature',
        id: s.id,
        slug: s.slug,
        title: s.title,
        tierOrStyle: 'Signature',
        priceCents: s.priceCents,
        thumbnail: s.thumbnail,
        options: s.options ?? { shape: ['Almondetto'], length: ['Medium'] },
        tags: s.tags ?? [],
        isOneOfOne: false,
        inventory: s.inventory ?? { trackType: 'made_to_order' },
      }));

    const onh = onHandProducts
      .filter(o => o.featured || o.tags?.some(t => /holiday|celeb/i.test(t)))
      .map(o => ({
        type: 'onhand',
        id: o.id,
        slug: o.slug,
        title: o.color || o.style || o.id,
        tierOrStyle: o.style || 'On Hand',
        priceCents: o.priceCents ?? parsePriceToCents(o.price),
        thumbnail: o.thumbnail,
        shape: o.shape,
        length: o.length,
        size: o.size,
        tags: o.tags ?? [],
        isOneOfOne: !!o.isOneOfOne,
        inventory: o.inventory ?? { trackType: 'stock', stockQty: 0 },
      }));

    return [...sig, ...onh];
  }, [onHandProducts]); // 👈 runs again when inventory updates

  // 2) Handle Quick Add
  const handleQuickAdd = (item, e) => {
    e.stopPropagation();

    if (item.type === 'signature') {
      const defaultShape = item.options?.shape?.[0] || 'Almondetto';
      const defaultLength = item.options?.length?.[0] || 'Medium';
      addToCart({
        slug: `signature/${item.slug}`,
        title: `Signature — ${item.title}`,
        price: formatPriceCents(item.priceCents),
        priceCents: item.priceCents,
        isSignature: true,
        thumbnail: item.thumbnail,
        shape: defaultShape,
        length: defaultLength,
        sizes: { sizeType: 'Medium', customMode: 'fitting', measurements: null },
        addons: '',
        inspo: null,
        notes: 'Signature recreation (as shown)',
        quantity: 1,
      });
    } else {
      const stockQty = item.inventory?.stockQty ?? 0;
      if (stockQty < 1) return; // sold out guard

      addToCart({
        slug: `onhand/${item.slug}`,
        title: `${item.tierOrStyle} — ${item.title}`,
        price: formatPriceCents(item.priceCents),
        priceCents: item.priceCents,
        isOneOfOne: item.isOneOfOne,
        thumbnail: item.thumbnail,
        shape: item.shape,
        length: item.length,
        sizes: { sizeType: item.size || 'Medium', customMode: 'fixed', measurements: null },
        quantity: 1,
      });
    }

    navigate('/nails/cart');
  };

  // 3) Handle opening details
  const openDetails = (item) => {
    if (item.type === 'signature') {
      navigate(`/nails/signature/${item.slug}`);
    } else {
      navigate(`/nails/onhand/${item.slug}`);
    }
  };

  return (
    <>
      <div className="product-detail-wrapper">
        <div className="px-4 py-10 max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-2">Featured Sets</h1>
          <h2 className="text-center max-w-xl mx-auto mb-10 mt-0 text-gray-600">
            Holiday drops, celeb-inspired looks, and one-of-one heat — refreshed often.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {featuredItems.map((it) => {
              const priceLabel = it.priceCents ? formatPriceCents(it.priceCents) : '';
              const badge = badgeFromTags(it.tags);
              const outOfStock =
                it.type === 'onhand' &&
                it.inventory?.trackType === 'stock' &&
                (it.inventory?.stockQty ?? 0) <= 0;

              return (
                <div
                  key={it.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDetails(it)}
                  onKeyDown={(e) => (e.key === 'Enter' ? openDetails(it) : null)}
                  className="bg-white rounded-2xl shadow hover:shadow-md transition overflow-hidden flex flex-col cursor-pointer"
                >
                  <div
                    className="w-full h-48 overflow-hidden"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox({ open: true, src: it.thumbnail, alt: it.title });
                    }}
                  >
                    <img
                      src={imgSrc(it.thumbnail)}
                      alt={it.title}
                      className="w-full h-full object-cover cursor-zoom-in"
                      loading="lazy"
                    />
                  </div>

                  <div className="p-4 flex-grow flex flex-col">
                    <h3 className="text-lg font-semibold mb-1">{it.title}</h3>

                    <p className="text-sm text-gray-600 mb-2 flex items-center gap-2 flex-wrap">
                      {it.tierOrStyle} • {priceLabel}
                      {badge && (
                        <span className="ml-1 inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-gray-100">
                          {badge}
                        </span>
                      )}
                      {it.type === 'onhand' && it.isOneOfOne && (
                        <span className="ml-1 inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-black/5">
                          1/1
                        </span>
                      )}
                      {outOfStock && (
                        <span className="ml-1 inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                          Sold out
                        </span>
                      )}
                    </p>

                    <div className="mt-auto flex gap-2">
                      <button
                        className={`btn-nene flex-1 whitespace-nowrap ${
                          outOfStock ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={(e) => !outOfStock && handleQuickAdd(it, e)}
                        disabled={outOfStock}
                      >
                        {outOfStock ? 'Sold Out' : 'Quick Add'}
                      </button>

                      <Link
                        to={it.type === 'signature'
                          ? `/nails/signature/${it.slug}`
                          : `/nails/onhand/${it.slug}`}
                        className="btn-nene flex-1 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {lightbox.open && (
            <Lightbox
              src={lightbox.src}
              alt={lightbox.alt}
              onClose={() => setLightbox({ open: false, src: '', alt: '' })}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
